<template>
  <div class="row q-col-gutter-md q-mt-sm">
    <div v-for="(metric, index) in metrics" :key="index" class="col-12 col-sm-4 col-md-3 col-lg-2">
      <q-card flat bordered>
        <q-card-section class="row items-center">
          <div class="title">{{ metric.label }}</div>
          <span class="q-ml-sm">
            <q-icon v-if="metric.tooltip" name="info" class="cursor-pointer" />
            <q-tooltip v-if="metric.tooltip" style="max-width: 200px">
              {{ metric.tooltip }}
            </q-tooltip>
          </span>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <q-input
            v-if="metric.editable && editMode === metric.label"
            v-model.number="metric.value"
            type="number"
            dense
            hide-bottom-space
            :rules="metric.rules"
            :step="metric.step ?? '1'"
            @blur="() => updateMetric({ key: metric.key, value: metric.value })"
            @keyup.enter="
              (event) =>
                updateMetric({ key: metric.key, value: Number(event.target.value) || metric.value })
            "
          />
          <q-btn
            v-else-if="metric.editable"
            dense
            unelevated
            no-caps
            class="q-pa-none"
            :disable="isLoading(revenueManagementResults)"
            @click="editMetric(metric.label)"
          >
            <q-tooltip v-if="isLoading(revenueManagementResults)">
              Please wait until results load to edit.
            </q-tooltip>
            <span class="text-weight-regular">
              {{ metric.formatter(metric.value) }}
              <q-icon name="edit" class="q-mb-xs q-ml-xs" size="16px" />
            </span>
          </q-btn>
          <div v-else class="q-py-xs">{{ metric.formatter(metric.value) }}</div>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<script lang="ts">
import { usePropertyReportStore } from '@/stores/propertyreport'
import { formatNumber, formatPercent } from '@/utils'
import { isLoading, isLoaded, isNotStarted } from '@/utils/loading'
import { estimatedRenewalsNew, occupancyOverTime, type PropertyDetailsResponse } from 'common'
import type { PropType } from 'vue'

interface RevenueManagementSettings {
  target_occupancy: number
  max_diff_percent: number
  timeframe_days: number
}

const isInteger = (val: number | null): boolean => {
  if (val === null) {
    return true
  }
  return Number.isInteger(val)
}

const isBetween = (val: string | number | null, min: number, max: number): boolean => {
  const num = Number(val)
  if (isNaN(num)) {
    return true
  }
  return num >= min && num <= max
}

interface Metric {
  label: string
  key: string
  value: number | null
  editable: boolean
  tooltip: string
  step?: string
  formatter?: (val: number) => string
  rules?: ((value: number | null | string) => boolean | string)[]
}

export default {
  name: 'PropertyCompareRecommendationHighlights',
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    }
  },
  data() {
    return {
      editMode: null as string | null,
      revenueManagementSettings: {
        target_occupancy: 0.95,
        max_diff_percent: 0.1,
        timeframe_days: 30
      } as RevenueManagementSettings
    }
  },
  mounted() {
    if (this.subjectDetail && isNotStarted(this.revenueManagementResults)) {
      usePropertyReportStore().getRevenueManagement(
        this.subjectDetail,
        [], // TODO: add comparables here. Disabled specifying comparables until we go full embedding
        this.revenueManagementSettings
      )
    }
  },
  computed: {
    revenueManagementResults() {
      return usePropertyReportStore().revenueManagement
    },
    metrics(): Metric[] {
      if (!this.subjectDetail) {
        return []
      }
      const occupancy = occupancyOverTime(
        this.subjectDetail,
        this.dateRange.from,
        this.dateRange.to
      )
      if (occupancy.length === 0) {
        return []
      }
      const lastExposure = occupancy[occupancy.length - 1].exposure
      let availableUnits = 0
      for (const unit of this.subjectDetail.building_availability) {
        if (unit.exit_market === null) {
          availableUnits++
        }
      }
      const estRenewals = estimatedRenewalsNew(
        this.subjectDetail.building_availability,
        this.subjectDetail.number_units
      )

      const objectives = {
        shortfall: 0,
        estimatedTrend: 0
      }
      if (isLoaded(this.revenueManagementResults)) {
        for (const objective of this.revenueManagementResults.data.objectives) {
          objectives.shortfall += objective.shortfall ?? 0
        }
      }

      return [
        {
          label: 'Occupancy',
          key: 'occupancy',
          value: lastExposure ? 1 - lastExposure : null,
          formatter: (val: number | null) => (val ? formatPercent(val, 1) : '-'),
          editable: false,
          tooltip: 'Computed as 1 - last exposure'
        },
        {
          label: 'Sustainable',
          key: 'target_occupancy',
          value: this.revenueManagementSettings.target_occupancy,
          formatter: (val: number) => formatPercent(val, 0),
          editable: true,
          tooltip: 'Target occupancy for the property. Edit to change your target occupancy.',
          step: '0.01',
          rules: [
            (value: number | null | string) => (value !== null && value !== '') || 'Required',
            (value: number) => isBetween(value, 0.5, 1) || 'Must be between 50% and 100%'
          ]
        },
        {
          label: 'Timeframe',
          key: 'timeframe_days',
          value: this.revenueManagementSettings.timeframe_days,
          formatter: (val: number) => `${val} days`,
          editable: true,
          tooltip: 'Timeframe to reach target occupancy. Edit to change the timeframe.',
          rules: [
            (value: number | null | string) => (value !== null && value !== '') || 'Required',
            (value: number) => isInteger(value) || 'Must be an integer',
            (value: number) => isBetween(value, 10, 90) || 'Must be between 10 and 90 days'
          ]
        },
        {
          label: 'Available Units',
          key: 'available_units',
          value: availableUnits,
          formatter: (val: number) => `${val} units`,
          editable: false,
          tooltip: 'Number of units currentlyavailable for leasing.'
        },
        {
          label: 'Est. Renewals',
          key: 'est_renewals',
          value: estRenewals?.percentage ?? null,
          formatter: (val: number | null) => (val ? `${formatPercent(val, 1)} of leases` : '-'),
          editable: false,
          tooltip: estRenewals
            ? `Estimated percentage of leases that we have seen in the trailing 12 months. Only ${estRenewals.validLeases - estRenewals.renewedUnits} of the units were marketed, implying ${estRenewals.renewedUnits} renewed their leases.`
            : 'We have not seen enough leases to estimate the renewal rate.'
        },
        {
          label: 'Shortfall',
          key: 'shortfall',
          value: objectives.shortfall,
          formatter: (val: number) => `${formatNumber(val)} leases`,
          editable: false,
          tooltip: 'Number of leases that need to be signed to reach the target occupancy.'
        }
      ]
    }
  },
  methods: {
    isLoading,
    editMetric(label: string) {
      this.editMode = label
    },
    updateMetric(metric: { key: keyof RevenueManagementSettings; value: number }) {
      this.editMode = null
      this.revenueManagementSettings[metric.key] = metric.value
      if (this.subjectDetail) {
        // force a refresh of the revenue management results
        usePropertyReportStore().getRevenueManagement(
          this.subjectDetail,
          [], // TODO: add comparables here. Disabled specifying comparables until we go full embedding
          this.revenueManagementSettings
        )
      }
    }
  }
}
</script>

<style scoped>
.title {
  font-weight: 500;
}
</style>
